import React, { useState } from 'react';
import { ChevronRight, ChevronDown, UserPlus, UserMinus, ArrowRightLeft, Users } from 'lucide-react';

function UserNode({ user, onAssignReport, onRemoveReport, onMoveReport, assignableUsers, isDirectorOrAbove, currentUser, theme, depth = 0 }) {
  const [expanded, setExpanded] = useState(depth < 2);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const hasReports = user.direct_reports?.length > 0;

  const roleColors = {
    admin: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    director: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    manager: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    associate: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    user: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };

  const handleAssign = () => {
    if (selectedUser) {
      onAssignReport(selectedUser, user.id);
      setSelectedUser('');
      setShowAssign(false);
    }
  };

  // Can add reports under this node? The node must be a manager+ role (associates can't have reports)
  const nodeCanBeSupervior = ['manager', 'director', 'admin'].includes(user.role);
  const canAssign = nodeCanBeSupervior && (
    currentUser?.admin ||
    (currentUser?.role === 'director' && ['director', 'manager'].includes(user.role)) ||
    (currentUser?.role === 'manager' && user.id === currentUser?.id)
  );

  return (
    <div style={{ marginLeft: depth * 24 }}>
      <div className={`flex items-center gap-2 py-2 px-3 rounded-lg group transition-colors ${theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className={`p-0.5 rounded ${hasReports ? 'visible' : 'invisible'}`}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        {/* Avatar */}
        {user.avatar_url ? (
          <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium">
            {(user.first_name || user.username)?.charAt(0)?.toUpperCase()}
          </div>
        )}

        {/* Name + Role + Departments */}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium">
            {user.first_name ? `${user.first_name} ${user.last_name}` : user.username}
          </span>
          <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${roleColors[user.role] || roleColors.user}`}>
            {user.role_display || user.role}
          </span>
          {(user.departments || []).map((d, i) => (
            <span
              key={i}
              className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
              title={d.service_area_name ? `${d.department_name} · ${d.service_area_name}` : d.department_name}
            >
              {d.department_name}{d.service_area_name ? ` · ${d.service_area_name}` : ''}
            </span>
          ))}
        </div>

        {/* Actions */}
        {canAssign && (
          <div className="hidden group-hover:flex items-center gap-1">
            <button
              onClick={() => setShowAssign(!showAssign)}
              className={`p-1 rounded transition-colors ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
              title="Add report"
            >
              <UserPlus size={14} />
            </button>
          </div>
        )}

        {/* Reports count */}
        {hasReports && (
          <span className={`text-[10px] ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
            {user.direct_reports.length} report{user.direct_reports.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Assign form */}
      {showAssign && (
        <div className={`ml-10 mb-2 flex items-center gap-2 p-2 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className={`flex-1 px-2 py-1 text-xs rounded border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
          >
            <option value="">Select user to add...</option>
            {assignableUsers
              .filter(u => u.id !== user.id && !user.direct_reports?.some(dr => dr.id === u.id))
              .map(u => (
                <option key={u.id} value={u.id}>
                  {u.first_name ? `${u.first_name} ${u.last_name}` : u.username} ({u.role})
                </option>
              ))}
          </select>
          <button onClick={handleAssign} disabled={!selectedUser} className="px-2 py-1 bg-blue-600 text-white text-xs rounded disabled:opacity-50">
            Add
          </button>
          <button onClick={() => setShowAssign(false)} className="px-2 py-1 text-xs rounded hover:bg-gray-200 dark:hover:bg-gray-700">
            Cancel
          </button>
        </div>
      )}

      {/* Children */}
      {expanded && hasReports && (
        <div>
          {user.direct_reports.map(report => (
            <div key={report.id} className="flex items-center">
              <div className="flex-1">
                <UserNode
                  user={report}
                  onAssignReport={onAssignReport}
                  onRemoveReport={onRemoveReport}
                  onMoveReport={onMoveReport}
                  assignableUsers={assignableUsers}
                  isDirectorOrAbove={isDirectorOrAbove}
                  currentUser={currentUser}
                  theme={theme}
                  depth={depth + 1}
                />
              </div>
              {canAssign && (
                <button
                  onClick={() => onRemoveReport(report.id, user.id)}
                  className="p-1 rounded text-red-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove from this supervisor"
                >
                  <UserMinus size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrgTreePanel({ hierarchy, loading, onAssignReport, onRemoveReport, onBulkAssign, onMoveReport, assignableUsers, isDirectorOrAbove, currentUser, theme }) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className={`h-12 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'}`} />
        ))}
      </div>
    );
  }

  // Build tree: only render root nodes (users whose supervisors are not in this result set)
  // The recursive direct_reports on each node handles nesting
  const hierarchyIds = new Set(hierarchy.map(u => u.id));
  const topLevel = hierarchy.filter(u => {
    if (!u.supervisors || u.supervisors.length === 0) return true;
    // Show at top level only if none of their supervisors are in the hierarchy
    return !u.supervisors.some(s => hierarchyIds.has(s.id));
  });
  const withReports = topLevel.length > 0 ? topLevel : hierarchy;

  if (hierarchy.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <Users size={48} className="mb-3 opacity-30" />
        <p className="text-sm">No reporting hierarchy set up yet</p>
        <p className="text-xs mt-1">Use the "My Team" tab to start adding reports</p>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border ${theme === 'dark' ? 'border-gray-700 bg-gray-850' : 'border-gray-200 bg-white'} p-4`}>
      {withReports.map(user => (
        <UserNode
          key={user.id}
          user={user}
          onAssignReport={onAssignReport}
          onRemoveReport={onRemoveReport}
          onMoveReport={onMoveReport}
          assignableUsers={assignableUsers}
          isDirectorOrAbove={isDirectorOrAbove}
          currentUser={currentUser}
          theme={theme}
        />
      ))}
    </div>
  );
}
